import { useQuery } from "@tanstack/react-query"

import { api } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import type { DocumentoExportable } from "@/componentes/exportar/modelo"
import { EstadoCarga, EstadoError, EstadoVacio } from "@/componentes/ui/Estados"
import { Insignia } from "@/componentes/ui/Insignia"
import { Tabla, Td, Th } from "@/componentes/ui/Tabla"
import { comoFechaHora } from "@/lib/fechas"

import { cifra } from "./descarga"

type Flete = {
  concepto: string | null
  cantidad: string | null
  costo_proveedor: string | null
  moneda: string | null
  proveedor: string | null
}

type AsignacionAgencia = {
  id: number
  numero: string
  asignado_en: string
  intermediario: string | null
  carga: { bl: string | null; estatus: string | null }
  fletes: Flete[]
  recibos: Partial<Record<"BORRADOR" | "EMITIDO" | "RECIBIDO" | "ANULADO", number>>
  reportado_en: string | null
  reporte_error: string | null
  aplicado: { proveedor_id: number | null; nota: string | null } | null
  vigente: boolean
}

/**
 * Una fila por renglón de flete (una carga con dos fletes sale en dos filas), con el
 * costo como número y la moneda aparte: leída de la tabla, esa celda mezcla cifras,
 * monedas y proveedores en un solo texto.
 */
function documentoDeAsignaciones(lista: AsignacionAgencia[]): DocumentoExportable {
  return {
    titulo: "Cargas asignadas",
    subtitulo: `Asignaciones del 2.0 a los intermediarios · ${lista.length} cargas`,
    secciones: [
      {
        titulo: "Cargas asignadas",
        columnas: [
          { titulo: "Expediente", tipo: "texto" },
          { titulo: "BL", tipo: "texto" },
          { titulo: "Estatus", tipo: "texto" },
          { titulo: "Intermediario", tipo: "texto" },
          { titulo: "Asignado", tipo: "fecha" },
          { titulo: "Flete", tipo: "monto" },
          { titulo: "Moneda", tipo: "texto" },
          { titulo: "Proveedor", tipo: "texto" },
          { titulo: "Recibos emitidos", tipo: "numero" },
          { titulo: "Reporte al 2.0", tipo: "texto" },
        ],
        filas: lista.flatMap((a) => {
          const emitidos = (a.recibos.EMITIDO ?? 0) + (a.recibos.RECIBIDO ?? 0)
          const reporte = a.reporte_error
            ? `Pendiente: ${a.reporte_error}`
            : a.reportado_en
              ? [`Enviado ${comoFechaHora(a.reportado_en)}`, a.aplicado?.nota]
                  .filter(Boolean)
                  .join(" · ")
              : null
          const base = [
            a.numero,
            a.carga.bl,
            a.carga.estatus?.replaceAll("_", " ") ?? null,
            a.intermediario,
            a.asignado_en.slice(0, 10),
          ]
          const fletes = a.fletes.length ? a.fletes : [null]
          return fletes.map((f) => ({
            celdas: [
              ...base,
              f ? Number(f.costo_proveedor ?? 0) * Math.max(1, Number(f.cantidad) || 1) : null,
              f?.moneda ?? null,
              f ? (f.proveedor ?? "sin proveedor") : null,
              emitidos,
              reporte,
            ],
          }))
        }),
      },
    ],
  }
}

/**
 * Las cargas asignadas en el 2.0 a los intermediarios (25/10): a quién, qué reportó y si el
 * 2.0 ya tomó su empresa como proveedor del flete. Se asignan y se deciden en el 2.0; aquí
 * se mira.
 */
export function AgenciaAsignaciones() {
  const lista = useQuery({
    queryKey: ["agencia-transporte", "asignaciones"],
    queryFn: () => api.get<AsignacionAgencia[]>("/api/v1/agencia-transporte/asignaciones"),
  })
  if (lista.isPending) return <EstadoCarga etiqueta="Cargando asignaciones" />
  if (lista.isError) {
    return (
      <EstadoError
        titulo="No se pudieron cargar"
        error={lista.error}
        onReintentar={() => lista.refetch()}
      />
    )
  }
  if (lista.data.length === 0) {
    return (
      <EstadoVacio
        titulo="Sin cargas asignadas"
        texto="En el 2.0, al pasar un expediente con flete a «Por despacho», se elige su intermediario y aparece aquí."
      />
    )
  }
  const datos = lista.data
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Exportar titulo="Cargas asignadas" documento={() => documentoDeAsignaciones(datos)} />
      </div>
      <Tabla etiqueta="Cargas asignadas">
        <thead>
          <tr>
            <Th>Expediente</Th>
            <Th>Intermediario</Th>
            <Th>Flete en la planilla</Th>
            <Th>Recibos</Th>
            <Th>Reporte al 2.0</Th>
          </tr>
        </thead>
        <tbody>
          {lista.data.map((a) => {
            const emitidos = (a.recibos.EMITIDO ?? 0) + (a.recibos.RECIBIDO ?? 0)
            return (
              <tr key={a.id}>
                <Td>
                  <span className="font-medium">{a.numero}</span>
                  <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
                    {[a.carga.bl, a.carga.estatus?.replaceAll("_", " ").toLowerCase()]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </Td>
                <Td>
                  {a.intermediario ?? "—"}
                  <span className="block text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
                    {comoFechaHora(a.asignado_en)}
                  </span>
                </Td>
                <Td className="tabular-nums">
                  {a.fletes.map((f, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: renglones del 2.0, sin id propio
                    <span key={i} className="block">
                      {cifra(Number(f.costo_proveedor ?? 0) * Math.max(1, Number(f.cantidad) || 1))}{" "}
                      {f.moneda} · {f.proveedor ?? "sin proveedor"}
                    </span>
                  ))}
                </Td>
                <Td>
                  {emitidos ? (
                    <Insignia tono="exito">
                      {emitidos} emitido{emitidos === 1 ? "" : "s"}
                    </Insignia>
                  ) : (
                    <Insignia tono="aviso">Por reportar</Insignia>
                  )}
                </Td>
                <Td>
                  {a.reporte_error ? (
                    <Insignia tono="peligro">Pendiente: {a.reporte_error.slice(0, 60)}</Insignia>
                  ) : a.reportado_en ? (
                    <span className="text-xs">
                      Enviado {comoFechaHora(a.reportado_en)}
                      {a.aplicado?.nota && (
                        <span className="block" style={{ color: "var(--warning)" }}>
                          {a.aplicado.nota}
                        </span>
                      )}
                    </span>
                  ) : (
                    "—"
                  )}
                </Td>
              </tr>
            )
          })}
        </tbody>
      </Tabla>
    </div>
  )
}
